import {
  Injectable, Injector,
  NgZone,
  OnDestroy, Type,
} from '@angular/core';
import { take, tap } from 'rxjs/operators';
import { FoxitPdfRendererBase } from './FoxitPdfRendererBase';
import {
  FoxitModule, FoxitModuleToken,
} from 'webcommon/lazy-load-commonjs/foxit';

import {
  ApBaseClass, ApDeferred, destroyManualInjector,
  FoxitLicenseDto, FoxitLicenseDtoToken,
} from 'webcommon/shared';

import {IPdfRenderer} from './IPdfRenderer';

export interface DeferredFoxitPdfRendererBase<TPdfRenderer extends FoxitPdfRendererBase> {
  // use result when you want to take an action on the renderer if it's initialized,
  // but if it's not, then you're fine with skipping the action
  result?: TPdfRenderer;

  // use resultPromise, if you want to wait until the renderer is initialized (it could already be initialized),
  // then take some action on it,
  // This promise resolves to the same instance as what is returned from init().
  resultPromise: Promise<TPdfRenderer>;

  // initialize the renderer; returns a promise that resolves when it's done with the creation of the main foxit class
  // This does not mean that it's completely done rendering/initializing.
  // element: HTMLElement to mount the pdf viewer on
  // The returned promise resolves to the same instance as resultPromise.
  init: (
    foxitSetup: FoxitLicenseDto,
    foxitModule: FoxitModule,
    element: HTMLElement,
    rendererClass: Type<TPdfRenderer>,
  ) => TPdfRenderer;
}

export type DeferredFoxitPdfRenderer<TPdfRenderer extends FoxitPdfRendererBase> = Readonly<DeferredFoxitPdfRendererBase<TPdfRenderer>>;

@Injectable({
  providedIn: 'root',
})
export class PdfRendererFactory extends ApBaseClass implements OnDestroy {
  private pdfRenderers = new Set<IPdfRenderer>();

  constructor(
    private readonly zone: NgZone,
    private moduleInjector: Injector,
  ) {
    super();
  }

  // This method should be preferred over createOnElement() because the result is easier to work with.
  // return initialization function to be called in ngOnInit, when HtmlElement is available
  // This allows this function to be called in the constructor, and immediately provide a promise
  // that can be referenced elsewhere in the code
  createDeferred<TPdfRenderer extends FoxitPdfRendererBase>(
  ): DeferredFoxitPdfRenderer<TPdfRenderer> {
    const deferred = new ApDeferred<TPdfRenderer>();
    const deferredPdfRenderer: DeferredFoxitPdfRendererBase<TPdfRenderer> = {
      resultPromise: deferred.promise,
      init: (
        foxitSetup,
        foxitModule,
        element,
        rendererClass,
      ) => {
        if (deferredPdfRenderer.result) {
          return deferredPdfRenderer.result;
        }

        const pdfRenderer = this.createOnElement(
          foxitSetup,
          foxitModule,
          element,
          rendererClass,
        );

        deferred.resolve(pdfRenderer);
        deferredPdfRenderer.result = pdfRenderer;
        return pdfRenderer;
      },
    };
    return deferredPdfRenderer;
  }

  // element: HTMLElement to mount the pdf viewer on
  // IMPORTANT: when taking actions based on data coming from this viewer, it will not be in ng zone.
  // It's allowing the import() of the library, and the creation of the PDFUI class to occur outside the ng zone,
  // because it's not related to angular at all, and could have performance issues if run in the ng zone.
  // In classes using this viewer where you're affecting bindings or other Angular stuff,
  // you need to call NgZone.run().
  createOnElement<TPdfRenderer extends FoxitPdfRendererBase>(
    foxitSetup: FoxitLicenseDto,
    foxitModule: FoxitModule,
    element: HTMLElement,
    rendererClass: Type<TPdfRenderer>,
  ): TPdfRenderer {
    const injector = Injector.create({
      name: 'PdfRendererFactory-PdfRendererInjector',
      parent: this.moduleInjector,
      providers: [
        {
          provide: FoxitModuleToken,
          useValue: foxitModule,
        },
        {
          provide: FoxitLicenseDtoToken,
          useValue: foxitSetup,
        },
        {
          provide: HTMLElement,
          useValue: element,
        },
        {
          provide: rendererClass,
          useClass: rendererClass,
        },
      ],
    });
    const pdfRenderer = this.zone.runOutsideAngular(() => {
      return injector.get(rendererClass);
    });

    this.pdfRenderers.add(pdfRenderer);
    pdfRenderer.onDestroy$.pipe(
      take(1),
      tap(() => {
        this.pdfRenderers.delete(pdfRenderer);
        // We created the injector to create the pdfRenderer,
        // so it makes sense to destroy it when the pdfRenderer gets destroyed.
        // The only problem is that it will try to destroy the pdfRenderer when destroying the injector.
        // So we only want all of this to happen once.
        destroyManualInjector(injector);
      }),
    ).subscribe();
    return pdfRenderer;
  }

  override ngOnDestroy(): void {
    this.pdfRenderers.forEach((pdfRenderer, _key, set) => {
      // todo: pdfRenderer.ngOnDestroy() => PDFUI.destroy returns a promise;
      // may have to await that at some point if necessary
      pdfRenderer.ngOnDestroy();
      set.delete(pdfRenderer);
    });
    super.ngOnDestroy();
  }
}
